import { EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { isSuppressed, type Maybe } from '@/types/domain'

/* Renders a field that the viewer's role may not see.
 *
 * The rule (AD-6, FR-29): a restricted audience's DTO has no such field at all,
 * so omission cannot be forgotten — but it carries an explicit hidden marker,
 * and the UI renders that marker rather than an empty cell. A Sales user seeing
 * a blank agent slot would read it as a data-entry error and go fix it; seeing
 * "Agent hidden" learns the rule once.
 *
 * This is stated plainly rather than disguised. Amounts stay visible on a Sales
 * user's own deals — only the identity is suppressed. */
export function SuppressedValue({
  value,
  /** What to say when suppressed. Defaults to the participant-slot wording;
   *  pass a translated string for carrier, vendor or contact slots. */
  label,
  className,
  fallback = '—',
}: {
  value: Maybe<string> | undefined
  label?: string
  className?: string
  fallback?: string
}) {
  const { t } = useTranslation()

  if (isSuppressed(value)) {
    return (
      <span
        className={cn(
          'text-text-muted inline-flex items-center gap-1 text-[12.5px] italic',
          className,
        )}
        title={t('common.suppressed.reason')}
      >
        <EyeOff size={12} aria-hidden />
        {label ?? t('common.suppressed.agent')}
      </span>
    )
  }

  if (!value) return <span className={cn('text-text-faint', className)}>{fallback}</span>

  return <span className={className}>{value}</span>
}
